import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  LogOut, Users, Sparkles, PhoneCall, TrendingUp, Loader2, ExternalLink, X,
  Image as ImageIcon, Plus, Trash2, Clock, AlertTriangle, Search, UserPlus, Download,
  Ban, Check, UserCheck, MessageSquare,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { BRAND } from "@/lib/data";
import PanoramaViewer from "@/components/PanoramaViewer";
import ProjectsTab from "@/pages/admin/ProjectsTab";

const STATUSES = ["New", "Contacted", "Follow-up", "Site Visit", "Quoted", "Converted", "Lost"];
const SOURCES = ["Website form", "WhatsApp", "Referral", "Walk-in", "Ads", "Other"];
const STATUS_COLORS = {
  New: "bg-[#F8C21C] text-[#143D63]",
  Contacted: "bg-[#143D63] text-[#F9F8F6]",
  "Follow-up": "bg-[#2563eb] text-white",
  "Site Visit": "bg-[#7c3aed] text-white",
  Quoted: "bg-[#0891b2] text-white",
  Converted: "bg-green-700 text-white",
  Lost: "bg-[#8B0000] text-white",
};

const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const off = d.getTimezoneOffset();
  return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
};
const isOverdue = (iso, status) =>
  iso && new Date(iso) < new Date() && !["Converted", "Lost"].includes(status);
const isToday = (iso) => {
  if (!iso) return false;
  const d = new Date(iso), n = new Date();
  return d.toDateString() === n.toDateString();
};

export default function AdminDashboard() {
  const { user, ready, logout } = useAuth();
  const nav = useNavigate();
  const isAdmin = user?.role === "admin";

  const [tab, setTab] = useState("leads");
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, today_callbacks: 0, overdue: 0, conversion_rate: 0 });
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [fStatus, setFStatus] = useState("All");
  const [fSource, setFSource] = useState("All");
  const [selected, setSelected] = useState(null);
  const [media, setMedia] = useState(null);

  useEffect(() => { if (ready && !user) nav("/admin"); }, [ready, user, nav]);

  const loadLeads = useCallback(async () => {
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (fStatus !== "All") params.set("status", fStatus);
    if (fSource !== "All") params.set("source", fSource);
    const [l, s] = await Promise.all([
      api.get(`/leads?${params.toString()}`),
      api.get("/leads/stats"),
    ]);
    setLeads(l.data);
    setStats(s.data);
  }, [search, fStatus, fSource]);

  const loadStaff = useCallback(async () => {
    if (!isAdmin) return;
    const r = await api.get("/users");
    setStaff(r.data);
  }, [isAdmin]);

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    Promise.all([loadLeads(), loadStaff()])
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || "Failed to load"))
      .finally(() => setLoading(false));
  }, [user, loadLeads, loadStaff]);

  const patchLead = async (id, updates) => {
    try {
      const { data } = await api.patch(`/leads/${id}`, updates);
      setLeads((ls) => ls.map((l) => (l._id === id ? data : l)));
      setSelected((s) => (s && s._id === id ? data : s));
      loadLeads();
      toast.success("Lead updated");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Update failed");
    }
  };

  const exportCsv = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (fStatus !== "All") params.set("status", fStatus);
      if (fSource !== "All") params.set("source", fSource);
      const res = await api.get(`/leads/export?${params.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "zebo-leads.csv";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Leads exported");
    } catch {
      toast.error("Export failed");
    }
  };

  const openMedia = async (id) => {
    setMedia("loading");
    try {
      const { data } = await api.get(`/leads/${id}/media`);
      setMedia({ ...data, view: "2d" });
    } catch {
      toast.error("Could not load media");
      setMedia(null);
    }
  };

  if (!ready || (user && loading)) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center relative z-10">
        <Loader2 className="animate-spin text-[#143D63]" size={36} />
      </div>
    );
  }

  const cards = [
    { key: "today", label: "Today's Callbacks", value: stats.today_callbacks, icon: PhoneCall },
    { key: "overdue", label: "Overdue", value: stats.overdue, icon: AlertTriangle },
    { key: "new", label: "New Leads", value: stats.new, icon: Sparkles },
    { key: "conv", label: "Conversion Rate", value: `${stats.conversion_rate}%`, icon: TrendingUp },
  ];

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#F9F8F6] relative z-10">
      <header className="bg-[#143D63] text-[#F9F8F6]">
        <div className="max-w-[1500px] mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={BRAND.logo} alt={BRAND.full} className="h-9 w-auto bg-[#F9F8F6] px-2 py-1" />
            <span className="font-serif text-2xl hidden sm:inline">Studio Dashboard</span>
          </div>
          <div className="flex items-center gap-3 sm:gap-5">
            <button data-testid="tab-leads" onClick={() => setTab("leads")} className={`text-sm font-medium ${tab === "leads" ? "text-[#F8C21C]" : "text-[#F9F8F6]/80"}`}>Leads</button>
            <button data-testid="tab-projects" onClick={() => setTab("projects")} className={`text-sm font-medium ${tab === "projects" ? "text-[#F8C21C]" : "text-[#F9F8F6]/80"}`}>Projects</button>
            <button data-testid="tab-analytics" onClick={() => setTab("analytics")} className={`text-sm font-medium ${tab === "analytics" ? "text-[#F8C21C]" : "text-[#F9F8F6]/80"}`}>Analytics</button>
            {isAdmin && <button data-testid="tab-customers" onClick={() => setTab("customers")} className={`text-sm font-medium ${tab === "customers" ? "text-[#F8C21C]" : "text-[#F9F8F6]/80"}`}>Customers</button>}
            {isAdmin && <button data-testid="tab-team" onClick={() => setTab("team")} className={`text-sm font-medium ${tab === "team" ? "text-[#F8C21C]" : "text-[#F9F8F6]/80"}`}>Team</button>}
            <Link to="/" className="text-sm text-[#F9F8F6]/80 hidden md:flex items-center gap-1">View site <ExternalLink size={14} /></Link>
            <button data-testid="admin-logout-btn" onClick={() => { logout(); nav("/admin"); }} className="flex items-center gap-2 bg-[#F8C21C] text-[#143D63] px-4 py-2 text-sm font-bold hover:bg-[#8B0000] hover:text-white transition-colors">
              <LogOut size={16} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1500px] mx-auto px-5 sm:px-8 py-10">
        {tab === "leads" && (
          <>
            <p className="overline text-[#8B0000] mb-2">Welcome back, {user?.name}</p>
            <h1 className="font-serif text-4xl sm:text-5xl text-[#143D63] mb-8">Lead Management</h1>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              {cards.map((c, i) => (
                <motion.div key={c.key} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                  data-testid={`stat-${c.key}`} className={`border bg-white p-6 ${c.key === "overdue" && stats.overdue > 0 ? "border-[#8B0000]" : "border-[#143D63]/15"}`}>
                  <c.icon className={`mb-3 ${c.key === "overdue" && stats.overdue > 0 ? "text-[#8B0000]" : "text-[#F8C21C]"}`} size={22} />
                  <p className="font-serif text-4xl text-[#143D63] leading-none">{c.value}</p>
                  <p className="text-sm text-[#143D63]/60 mt-2">{c.label}</p>
                </motion.div>
              ))}
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-3 mb-5 items-center">
              <div className="relative flex-1 min-w-[200px]">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#143D63]/40" />
                <input data-testid="lead-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, phone, email, city…"
                  className="w-full border border-[#143D63]/20 bg-white pl-9 pr-4 py-2.5 text-sm focus:border-[#F8C21C] focus:outline-none" />
              </div>
              <select data-testid="filter-status" value={fStatus} onChange={(e) => setFStatus(e.target.value)} className="border border-[#143D63]/20 bg-white px-3 py-2.5 text-sm focus:border-[#F8C21C] focus:outline-none">
                <option>All</option>{STATUSES.map((s) => <option key={s}>{s}</option>)}
              </select>
              <select data-testid="filter-source" value={fSource} onChange={(e) => setFSource(e.target.value)} className="border border-[#143D63]/20 bg-white px-3 py-2.5 text-sm focus:border-[#F8C21C] focus:outline-none">
                <option>All</option>{SOURCES.map((s) => <option key={s}>{s}</option>)}
              </select>
              <button data-testid="export-csv-btn" onClick={exportCsv} className="inline-flex items-center gap-2 bg-[#143D63] text-[#F9F8F6] px-4 py-2.5 text-sm font-bold hover:bg-[#8B0000] transition-colors">
                <Download size={16} /> Export CSV
              </button>
            </div>

            {/* Leads table */}
            <div className="border border-[#143D63]/15 bg-white overflow-hidden">
              {leads.length === 0 ? (
                <div className="p-16 text-center text-[#143D63]/60"><Users className="mx-auto mb-4 opacity-40" size={40} />No leads match your filters.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-[#143D63]/5 text-left text-[#143D63]/70">
                        <th className="px-5 py-4 font-semibold">Client</th>
                        <th className="px-5 py-4 font-semibold hidden md:table-cell">Source</th>
                        <th className="px-5 py-4 font-semibold hidden lg:table-cell">Assigned</th>
                        <th className="px-5 py-4 font-semibold hidden lg:table-cell">Callback</th>
                        <th className="px-5 py-4 font-semibold">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {leads.map((l) => (
                        <tr key={l._id} data-testid={`lead-row-${l._id}`} onClick={() => setSelected(l)} className="border-t border-[#143D63]/10 cursor-pointer hover:bg-[#F9F8F6] align-top">
                          <td className="px-5 py-4">
                            <p className="font-semibold text-[#143D63] flex items-center gap-2">{l.name}{l.has_design && <ImageIcon size={13} className="text-[#8B0000]" />}</p>
                            <p className="text-[#143D63]/50 text-xs">{l.phone} · {l.city || "—"}</p>
                          </td>
                          <td className="px-5 py-4 hidden md:table-cell text-[#143D63]/80">{l.source}</td>
                          <td className="px-5 py-4 hidden lg:table-cell text-[#143D63]/80">{l.assigned_to_name || <span className="text-[#143D63]/40">Unassigned</span>}</td>
                          <td className="px-5 py-4 hidden lg:table-cell">
                            {l.callback_at ? (
                              <span className={`text-xs inline-flex items-center gap-1 ${isOverdue(l.callback_at, l.status) ? "text-[#8B0000] font-bold" : isToday(l.callback_at) ? "text-[#143D63] font-bold" : "text-[#143D63]/70"}`}>
                                <Clock size={12} /> {new Date(l.callback_at).toLocaleString([], { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                              </span>
                            ) : <span className="text-[#143D63]/40 text-xs">—</span>}
                          </td>
                          <td className="px-5 py-4"><span className={`px-2.5 py-1 text-xs font-bold ${STATUS_COLORS[l.status] || "bg-gray-200"}`}>{l.status}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}

        {tab === "analytics" && <AnalyticsTab />}
        {tab === "projects" && <ProjectsTab />}
        {tab === "customers" && isAdmin && <CustomersTab />}
        {tab === "team" && isAdmin && <TeamTab staff={staff} reload={loadStaff} />}
      </main>

      {/* Lead detail drawer */}
      <AnimatePresence>
        {selected && (
          <LeadDrawer
            lead={selected}
            staff={staff}
            isAdmin={isAdmin}
            onClose={() => setSelected(null)}
            onPatch={patchLead}
            onOpenMedia={openMedia}
            onNoteAdded={(note) => setSelected((s) => ({ ...s, notes: [...(s.notes || []), note] }))}
          />
        )}
      </AnimatePresence>

      {/* Media modal */}
      {media && (
        <div data-testid="media-modal" onClick={() => setMedia(null)} className="fixed inset-0 z-[60] bg-[#0A0A0A]/85 flex items-center justify-center p-5">
          <div onClick={(e) => e.stopPropagation()} className="bg-[#F9F8F6] w-full max-w-3xl">
            <div className="flex items-center justify-between px-5 py-4 bg-[#143D63] text-[#F9F8F6]">
              <div className="flex items-center gap-3">
                <span className="font-serif text-xl">Client's AI design</span>
                {media !== "loading" && media.panorama && (
                  <div className="flex border border-[#F9F8F6]/30">
                    <button onClick={() => setMedia((m) => ({ ...m, view: "2d" }))} className={`px-3 py-1 text-xs font-bold ${media.view === "2d" ? "bg-[#F8C21C] text-[#143D63]" : ""}`}>2D</button>
                    <button data-testid="media-360-btn" onClick={() => setMedia((m) => ({ ...m, view: "360" }))} className={`px-3 py-1 text-xs font-bold ${media.view === "360" ? "bg-[#F8C21C] text-[#143D63]" : ""}`}>360°</button>
                  </div>
                )}
              </div>
              <button onClick={() => setMedia(null)} className="hover:text-[#F8C21C]"><X size={22} /></button>
            </div>
            <div className="p-4">
              {media === "loading" ? (
                <div className="h-72 flex items-center justify-center"><Loader2 className="animate-spin text-[#143D63]" size={30} /></div>
              ) : media.view === "360" && media.panorama ? (
                <div className="w-full h-[60vh]"><PanoramaViewer src={media.panorama} /></div>
              ) : media.image ? (
                <img src={media.image} alt="AI design" className="w-full object-contain max-h-[70vh]" />
              ) : (
                <p className="text-center text-[#143D63]/60 py-12">No design attached.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function LeadDrawer({ lead, staff, isAdmin, onClose, onPatch, onOpenMedia, onNoteAdded }) {
  const [note, setNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const field = "w-full border border-[#143D63]/20 bg-[#F9F8F6] px-3 py-2 text-sm focus:border-[#F8C21C] focus:outline-none";

  const addNote = async () => {
    if (!note.trim()) return;
    setSavingNote(true);
    try {
      const { data } = await api.post(`/leads/${lead._id}/notes`, { text: note });
      onNoteAdded(data);
      setNote("");
      toast.success("Note added");
    } catch (e) {
      toast.error("Could not add note");
    } finally {
      setSavingNote(false);
    }
  };

  return (
    <>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} className="fixed inset-0 z-50 bg-[#0A0A0A]/50" />
      <motion.aside
        data-testid="lead-drawer"
        initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }} transition={{ type: "tween", duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        className="fixed top-0 right-0 bottom-0 z-50 w-full max-w-md bg-[#F9F8F6] overflow-y-auto"
      >
        <div className="flex items-center justify-between px-6 py-5 bg-[#143D63] text-[#F9F8F6] sticky top-0 z-10">
          <div>
            <p className="overline text-[#F8C21C]">{lead.source}</p>
            <h2 className="font-serif text-2xl">{lead.name}</h2>
          </div>
          <button data-testid="drawer-close" onClick={onClose} className="hover:text-[#F8C21C]"><X size={22} /></button>
        </div>

        <div className="p-6 space-y-6">
          <div className="text-sm text-[#143D63]/80 space-y-1">
            <p><b>Phone:</b> {lead.phone}</p>
            {lead.email && <p><b>Email:</b> {lead.email}</p>}
            {lead.city && <p><b>City:</b> {lead.city}</p>}
            {lead.address && <p><b>Address:</b> {lead.address}</p>}
            {lead.scope && <p><b>Scope:</b> {lead.scope} · {lead.property_type || "—"} · {lead.budget || "—"}</p>}
            {lead.referred_by_code && (
              <p data-testid="lead-referred-by" className="bg-[#F8C21C]/15 border border-[#F8C21C]/50 px-2 py-1.5 inline-block">
                <b>🎁 Referred by:</b> {lead.referred_by_name || lead.referred_by_email} · code <span className="font-bold text-[#8B0000]">{lead.referred_by_code}</span>
              </p>
            )}
            {lead.message && <p className="text-[#143D63]/70 pt-1">"{lead.message}"</p>}
          </div>

          {lead.has_design && (
            <button data-testid="drawer-view-design" onClick={() => onOpenMedia(lead._id)} className="inline-flex items-center gap-2 bg-[#FFF7DA] text-[#8B0000] border border-[#F8C21C] px-3 py-2 text-xs font-bold hover:bg-[#F8C21C]">
              <ImageIcon size={14} /> View attached AI design {lead.panorama_image ? "(+360°)" : ""}
            </button>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="overline text-[#143D63]/60 mb-1 block">Status</label>
              <select data-testid="drawer-status" className={field} value={lead.status} onChange={(e) => onPatch(lead._id, { status: e.target.value })}>
                {STATUSES.map((s) => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="overline text-[#143D63]/60 mb-1 block">Source</label>
              <select data-testid="drawer-source" className={field} value={lead.source} onChange={(e) => onPatch(lead._id, { source: e.target.value })}>
                {SOURCES.map((s) => <option key={s}>{s}</option>)}
              </select>
            </div>
          </div>

          {isAdmin && (
            <div>
              <label className="overline text-[#143D63]/60 mb-1 block">Assign to</label>
              <select data-testid="drawer-assign" className={field} value={lead.assigned_to || ""} onChange={(e) => onPatch(lead._id, { assigned_to: e.target.value || null })}>
                <option value="">Unassigned</option>
                {staff.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.role})</option>)}
              </select>
            </div>
          )}

          <div>
            <label className="overline text-[#143D63]/60 mb-1 block">Callback date & time</label>
            <input
              data-testid="drawer-callback"
              type="datetime-local"
              className={field}
              value={toLocalInput(lead.callback_at)}
              onChange={(e) => onPatch(lead._id, { callback_at: e.target.value ? new Date(e.target.value).toISOString() : null })}
            />
            <p className="text-xs text-[#143D63]/50 mt-1">A WhatsApp + email reminder is sent at this time (and 30 min before).</p>
          </div>

          {/* Notes timeline */}
          <div>
            <label className="overline text-[#143D63]/60 mb-2 block">Activity / Notes</label>
            <div className="flex gap-2 mb-3">
              <input data-testid="note-input" className={field} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note…" onKeyDown={(e) => e.key === "Enter" && addNote()} />
              <button data-testid="note-add-btn" onClick={addNote} disabled={savingNote} className="bg-[#143D63] text-[#F9F8F6] px-4 disabled:opacity-60"><Plus size={16} /></button>
            </div>
            <div className="space-y-3">
              {(lead.notes || []).slice().reverse().map((n, i) => (
                <div key={i} className="border-l-2 border-[#F8C21C] pl-3">
                  <p className="text-sm text-[#143D63]">{n.text}</p>
                  <p className="text-xs text-[#143D63]/50">{n.by} · {new Date(n.at).toLocaleString()}</p>
                </div>
              ))}
              {(!lead.notes || lead.notes.length === 0) && <p className="text-sm text-[#143D63]/40">No activity yet.</p>}
            </div>
          </div>
        </div>
      </motion.aside>
    </>
  );
}

function BarList({ data, colors, testid }) {
  const entries = Object.entries(data || {}).sort((a, b) => b[1] - a[1]);
  const max = Math.max(1, ...entries.map(([, v]) => v));
  if (entries.length === 0) return <p className="text-sm text-[#143D63]/40">No data yet.</p>;
  return (
    <div className="space-y-3" data-testid={testid}>
      {entries.map(([k, v]) => (
        <div key={k}>
          <div className="flex justify-between text-sm mb-1">
            <span className="text-[#143D63]/80">{k}</span>
            <span className="font-bold text-[#143D63]">{v}</span>
          </div>
          <div className="h-2.5 bg-[#143D63]/10 overflow-hidden">
            <div className="h-full" style={{ width: `${(v / max) * 100}%`, background: colors?.[k] || "#F8C21C" }} />
          </div>
        </div>
      ))}
    </div>
  );
}

function TrendChart({ daily }) {
  const W = 640, H = 160, P = 8;
  const counts = daily.map((d) => d.count);
  const max = Math.max(1, ...counts);
  const stepX = (W - P * 2) / Math.max(1, daily.length - 1);
  const pts = daily.map((d, i) => {
    const x = P + i * stepX;
    const y = H - P - (d.count / max) * (H - P * 2);
    return [x, y];
  });
  const line = pts.map((p, i) => `${i === 0 ? "M" : "L"}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(" ");
  const area = `${line} L${(P + (daily.length - 1) * stepX).toFixed(1)},${H - P} L${P},${H - P} Z`;
  const totalWindow = counts.reduce((a, b) => a + b, 0);
  return (
    <div data-testid="trend-chart">
      <p className="text-sm text-[#143D63]/60 mb-3">{totalWindow} new leads in the last 30 days · peak {max}/day</p>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" preserveAspectRatio="none">
        <defs>
          <linearGradient id="trendFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#F8C21C" stopOpacity="0.45" />
            <stop offset="100%" stopColor="#F8C21C" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={area} fill="url(#trendFill)" />
        <path d={line} fill="none" stroke="#143D63" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
        {pts.map((p, i) => daily[i].count > 0 && <circle key={i} cx={p[0]} cy={p[1]} r="2.5" fill="#8B0000" />)}
      </svg>
      <div className="flex justify-between text-xs text-[#143D63]/40 mt-1">
        <span>{new Date(daily[0]?.date).toLocaleDateString([], { day: "2-digit", month: "short" })}</span>
        <span>Today</span>
      </div>
    </div>
  );
}

function AnalyticsTab() {
  const [data, setData] = useState(null);
  const STATUS_HEX = {
    New: "#F8C21C", Contacted: "#143D63", "Follow-up": "#2563eb", "Site Visit": "#7c3aed",
    Quoted: "#0891b2", Converted: "#15803d", Lost: "#8B0000",
  };

  useEffect(() => {
    api.get("/leads/analytics").then((r) => setData(r.data)).catch(() => toast.error("Could not load analytics"));
  }, []);

  if (!data) {
    return <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-[#143D63]" size={32} /></div>;
  }

  const kpis = [
    { label: "Total Leads", value: data.total },
    { label: "Last 30 Days", value: data.this_month },
    { label: "Converted", value: data.converted },
    { label: "Conversion Rate", value: `${data.conversion_rate}%` },
  ];

  return (
    <div data-testid="analytics-tab">
      <h1 className="font-serif text-4xl sm:text-5xl text-[#143D63] mb-8">Lead Analytics</h1>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {kpis.map((k) => (
          <div key={k.label} className="border border-[#143D63]/15 bg-white p-6">
            <p className="font-serif text-4xl text-[#143D63] leading-none">{k.value}</p>
            <p className="text-sm text-[#143D63]/60 mt-2">{k.label}</p>
          </div>
        ))}
      </div>
      <div className="border border-[#143D63]/15 bg-white p-6 mb-8">
        <p className="overline text-[#8B0000] mb-4">New leads over time</p>
        <TrendChart daily={data.daily} />
      </div>
      <div className="grid md:grid-cols-2 gap-6">
        <div className="border border-[#143D63]/15 bg-white p-6">
          <p className="overline text-[#8B0000] mb-5">Leads by source</p>
          <BarList data={data.by_source} testid="source-bars" />
        </div>
        <div className="border border-[#143D63]/15 bg-white p-6">
          <p className="overline text-[#8B0000] mb-5">Pipeline by status</p>
          <BarList data={data.by_status} colors={STATUS_HEX} testid="status-bars" />
        </div>
      </div>
    </div>
  );
}

function TeamTab({ staff, reload }) {
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", whatsapp: "", role: "staff" });
  const [saving, setSaving] = useState(false);
  const field = "w-full border border-[#143D63]/20 bg-white px-3 py-2.5 text-sm focus:border-[#F8C21C] focus:outline-none";
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const create = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password) { toast.error("Name, email and password required"); return; }
    setSaving(true);
    try {
      await api.post("/users", form);
      toast.success("Team member added");
      setForm({ name: "", email: "", password: "", phone: "", whatsapp: "", role: "staff" });
      reload();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not add user");
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    try { await api.delete(`/users/${id}`); toast.success("Removed"); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Delete failed"); }
  };

  return (
    <div data-testid="team-tab">
      <h1 className="font-serif text-4xl sm:text-5xl text-[#143D63] mb-8">Team</h1>
      <div className="grid lg:grid-cols-12 gap-8">
        <div className="lg:col-span-5">
          <form onSubmit={create} className="border border-[#143D63]/15 bg-white p-6 space-y-4">
            <p className="overline text-[#8B0000] flex items-center gap-2"><UserPlus size={14} /> Add team member</p>
            <input data-testid="staff-name" className={field} placeholder="Full name" value={form.name} onChange={set("name")} />
            <input data-testid="staff-email" className={field} placeholder="Email" type="email" value={form.email} onChange={set("email")} />
            <input data-testid="staff-password" className={field} placeholder="Temporary password" value={form.password} onChange={set("password")} />
            <input data-testid="staff-phone" className={field} placeholder="Phone (+91…)" value={form.phone} onChange={set("phone")} />
            <input data-testid="staff-whatsapp" className={field} placeholder="WhatsApp (+91…) — for reminders" value={form.whatsapp} onChange={set("whatsapp")} />
            <select data-testid="staff-role" className={field} value={form.role} onChange={set("role")}>
              <option value="staff">Staff (sees only assigned leads)</option>
              <option value="admin">Admin (full access)</option>
            </select>
            <button data-testid="staff-create-btn" disabled={saving} className="w-full bg-[#F8C21C] text-[#143D63] px-6 py-3 font-bold text-sm hover:bg-[#143D63] hover:text-[#F9F8F6] transition-colors disabled:opacity-60">
              {saving ? "Adding…" : "Add Member"}
            </button>
          </form>
        </div>
        <div className="lg:col-span-7">
          <div className="border border-[#143D63]/15 bg-white divide-y divide-[#143D63]/10">
            {staff.map((s) => (
              <div key={s.id} data-testid={`staff-row-${s.id}`} className="flex items-center justify-between px-5 py-4">
                <div>
                  <p className="font-semibold text-[#143D63]">{s.name} <span className={`ml-2 text-xs px-2 py-0.5 ${s.role === "admin" ? "bg-[#143D63] text-white" : "bg-[#F8C21C] text-[#143D63]"}`}>{s.role}</span></p>
                  <p className="text-xs text-[#143D63]/60">{s.email} · {s.whatsapp || s.phone || "no phone"}</p>
                </div>
                {s.role !== "admin" && (
                  <button data-testid={`staff-delete-${s.id}`} onClick={() => del(s.id)} className="text-[#8B0000] hover:opacity-70"><Trash2 size={18} /></button>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function CustomersTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [viewing, setViewing] = useState(null); // customer being drilled into

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/customers");
      setRows(data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load customers");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleBlock = async (c) => {
    const willBlock = !c.blocked;
    const reason = willBlock ? (window.prompt("Reason (optional):") || "Blocked by admin") : undefined;
    try {
      const { data } = await api.patch(`/customers/${c.id}/block`, { blocked: willBlock, reason });
      setRows((rs) => rs.map((r) => (r.id === c.id ? data : r)));
      toast.success(willBlock ? "Customer blocked" : "Customer unblocked");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to update");
    }
  };

  const convert = async (c) => {
    if (!window.confirm(`Create a lead for ${c.name} (${c.email})?`)) return;
    try {
      const { data } = await api.post(`/customers/${c.id}/convert-to-lead`);
      toast.success(`Lead created${data.assigned_to ? ` and assigned to ${data.assigned_to}` : ""}`);
      setRows((rs) => rs.map((r) => (r.id === c.id ? { ...r, converted_to_lead: true } : r)));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to convert");
    }
  };

  const filtered = rows.filter((r) => {
    if (!q.trim()) return true;
    const s = q.toLowerCase();
    return [r.name, r.email, r.phone, r.city].filter(Boolean).some((v) => String(v).toLowerCase().includes(s));
  });

  return (
    <div data-testid="customers-tab">
      <p className="overline text-[#8B0000] mb-2">Studio users</p>
      <h1 className="font-serif text-4xl sm:text-5xl text-[#143D63] mb-2">Customers</h1>
      <p className="text-[#143D63]/60 mb-8">Everyone who signed up in the AI Studio portal. Convert power users into leads, or block bad actors.</p>

      <div className="flex items-center gap-3 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search size={16} className="absolute top-3 left-3 text-[#143D63]/50" />
          <input
            data-testid="customers-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, email, phone or city"
            className="w-full border border-[#143D63]/20 bg-white pl-9 pr-4 py-2.5 text-sm focus:border-[#F8C21C] focus:outline-none"
          />
        </div>
        <span className="text-sm text-[#143D63]/60">{filtered.length} of {rows.length}</span>
      </div>

      <div className="bg-white border border-[#143D63]/15 overflow-hidden">
        {loading ? (
          <div className="p-16 flex items-center justify-center"><Loader2 className="animate-spin text-[#F8C21C]" size={30} /></div>
        ) : filtered.length === 0 ? (
          <div className="p-16 text-center text-[#143D63]/60">No customers yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#F9F8F6] text-[#143D63]/70 text-left">
                <tr>
                  <th className="px-5 py-4 font-semibold">Customer</th>
                  <th className="px-5 py-4 font-semibold hidden md:table-cell">Phone / City</th>
                  <th className="px-5 py-4 font-semibold">Gens</th>
                  <th className="px-5 py-4 font-semibold">Status</th>
                  <th className="px-5 py-4 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => (
                  <tr key={c.id} data-testid={`customer-row-${c.id}`} className="border-t border-[#143D63]/10">
                    <td className="px-5 py-4">
                      <p className="font-semibold text-[#143D63]">{c.name}</p>
                      <p className="text-[#143D63]/60 text-xs">{c.email}</p>
                    </td>
                    <td className="px-5 py-4 hidden md:table-cell text-[#143D63]/80 text-xs">
                      {c.phone || "—"}<br />{c.city || "—"}
                    </td>
                    <td className="px-5 py-4 text-[#143D63] font-semibold">{c.gen_count}</td>
                    <td className="px-5 py-4">
                      {c.blocked ? (
                        <span className="inline-flex items-center gap-1 bg-[#8B0000] text-white px-2 py-0.5 text-xs font-bold">
                          <Ban size={10} /> Blocked
                        </span>
                      ) : c.converted_to_lead ? (
                        <span className="inline-flex items-center gap-1 bg-green-700 text-white px-2 py-0.5 text-xs font-bold">
                          <Check size={10} /> Lead
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 bg-[#F8C21C] text-[#143D63] px-2 py-0.5 text-xs font-bold">Active</span>
                      )}
                    </td>
                    <td className="px-5 py-4 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button
                          data-testid={`customer-view-${c.id}`}
                          onClick={() => setViewing(c)}
                          className="border border-[#143D63]/20 px-2.5 py-1.5 text-xs hover:bg-[#143D63] hover:text-white transition-colors"
                          title="View generations"
                        >
                          <ImageIcon size={13} />
                        </button>
                        {!c.converted_to_lead && (
                          <button
                            data-testid={`customer-convert-${c.id}`}
                            onClick={() => convert(c)}
                            className="border border-[#143D63]/20 px-2.5 py-1.5 text-xs hover:bg-[#143D63] hover:text-white transition-colors"
                            title="Convert to lead"
                          >
                            <UserCheck size={13} />
                          </button>
                        )}
                        <button
                          data-testid={`customer-block-${c.id}`}
                          onClick={() => toggleBlock(c)}
                          className={`border px-2.5 py-1.5 text-xs transition-colors ${c.blocked ? "border-green-700 text-green-700 hover:bg-green-700 hover:text-white" : "border-[#8B0000] text-[#8B0000] hover:bg-[#8B0000] hover:text-white"}`}
                          title={c.blocked ? "Unblock" : "Block"}
                        >
                          {c.blocked ? <Check size={13} /> : <Ban size={13} />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <AnimatePresence>
        {viewing && <CustomerGenerationsDrawer customer={viewing} onClose={() => setViewing(null)} />}
      </AnimatePresence>
    </div>
  );
}

function CustomerGenerationsDrawer({ customer, onClose }) {
  const [gens, setGens] = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get(`/customers/${customer.id}/generations`)
      .then((r) => setGens(r.data || []))
      .catch(() => setGens([]));
  }, [customer.id]);

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onClose}
      className="fixed inset-0 z-[70] bg-[#0A0A0A]/80 flex items-center justify-center p-4"
      data-testid="customer-gens-drawer"
    >
      <motion.div
        initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 30, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-[#F9F8F6] w-full max-w-6xl max-h-[92vh] overflow-hidden flex flex-col"
      >
        <div className="bg-[#143D63] text-[#F9F8F6] px-6 py-4 flex items-center justify-between">
          <div>
            <p className="overline text-[#F8C21C] text-[10px]">Generations by</p>
            <p className="font-serif text-xl">{customer.name} <span className="text-[#F9F8F6]/60 text-sm">· {customer.email}</span></p>
          </div>
          <button onClick={onClose} className="hover:text-[#F8C21C]" data-testid="customer-gens-close"><X size={22} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          {gens === null ? (
            <div className="flex items-center justify-center py-24"><Loader2 size={30} className="animate-spin text-[#F8C21C]" /></div>
          ) : gens.length === 0 ? (
            <p className="text-center text-[#143D63]/60 py-24">No generations yet.</p>
          ) : selected ? (
            <div>
              <button onClick={() => setSelected(null)} className="text-sm text-[#8B0000] link-underline mb-4">&larr; Back to all generations</button>
              <div className="grid md:grid-cols-2 gap-6">
                <img src={selected.image} alt="gen" className="w-full border border-[#143D63]/15" />
                {selected.panorama && (
                  <div className="border border-[#143D63]/15 aspect-square"><PanoramaViewer src={selected.panorama} /></div>
                )}
              </div>
              <div className="mt-4 text-sm text-[#143D63]/80 space-y-1">
                <p><b>Mode:</b> {selected.mode}</p>
                {selected.style && <p><b>Style:</b> {selected.style}</p>}
                {selected.room_type && <p><b>Room / element:</b> {selected.room_type}</p>}
                {selected.prompt && <p><b>Prompt:</b> {selected.prompt}</p>}
                <p><b>When:</b> {new Date(selected.created_at).toLocaleString()}</p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {gens.map((g) => (
                <button
                  key={g.id}
                  onClick={() => setSelected(g)}
                  className="group relative aspect-square overflow-hidden border border-[#143D63]/15 bg-white"
                  data-testid={`gen-${g.id}`}
                >
                  <img src={g.image} alt={g.mode} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                  <span className="absolute bottom-1 left-1 bg-[#0A0A0A]/70 text-[#F9F8F6] text-[10px] px-2 py-0.5">{g.mode}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

